'use client';

import {
  YouMightLikeWaterFall,
  type YouMightLikeTrackingHandlers,
} from '@/app/cart/components/YouMightLikeWaterFall';
import { normalizeErrorPagePath } from '@/components/ErrorPage/errorPage';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import { reportSentryError } from '@/services/sentry';
import { env } from '@/utils/env';
import { useLocale } from 'next-intl';
import { usePathname } from 'next/navigation';
import { useContext, type ErrorInfo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

interface ErrorPageRecommendationsProps {
  onItemClick?: () => void;
}

const ERROR_PAGE_TRACKING = {
  collectScene: '异常页面猜你喜欢推荐商品',
  onItemClick: () => undefined,
  onProductInView: () => undefined,
  onProductValidExposure: () => undefined,
  onModelExp: () => undefined,
} satisfies YouMightLikeTrackingHandlers;

/** 错误页复用购物车推荐样式和商品卡片，但请求不携带 scene。 */
export default function ErrorPageRecommendations({ onItemClick }: ErrorPageRecommendationsProps) {
  const { deviceType } = useContext(ResponsiveContext);
  const currencyReady = useStore((state) => state.currencyReady);
  const locale = useLocale();
  const pathname = usePathname();

  if (!currencyReady) return null;

  const handleRecommendationsError = (error: unknown, info: ErrorInfo) => {
    reportSentryError(error, {
      domain: 'recommendation',
      stage: 'error_page_render',
      tags: {
        'error.page': normalizeErrorPagePath(pathname),
        'error.component': 'error_page_recommendations',
        'error.device_type': deviceType,
        'error.locale': locale,
        'error.site': env.NEXT_PUBLIC_SITE_NAME ?? 'unknown',
      },
      extra: {
        component_stack: info.componentStack,
      },
    });
  };

  return (
    <ErrorBoundary fallback={null} onError={handleRecommendationsError}>
      <YouMightLikeWaterFall
        deviceType={deviceType}
        frameWidth="100%"
        recommendList={[]}
        recommendationStrategy="error-page"
        tracking={ERROR_PAGE_TRACKING}
        onItemClick={onItemClick}
      />
    </ErrorBoundary>
  );
}
